/** What one declared setting admits. Every rule here mirrors the sdk's own `setting_refusal`. */

import type {
  ScalarSettingType,
  ScalarSettingValue,
  SettingField,
  SettingValue,
} from "../types";

/** The one shape every consumer of a color setting has to agree on. `manifest._COLOR_PATTERN`. */
export const COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

export function isScalar(value: unknown): value is ScalarSettingValue {
  const kind = typeof value;
  return kind === "string" || kind === "number" || kind === "boolean";
}

/** The scalar this field holds: its own type, or what its list names with `of`. */
export function scalarTypeOf(field: SettingField): ScalarSettingType {
  return field.type === "list" ? (field.of ?? "string") : field.type;
}

/** Whether a requirement binding fills this rather than a person. `SettingField.is_supplied`. */
export function isSupplied(field: SettingField): boolean {
  return field.from != null && field.from !== "";
}

/** The entries of a list value, dropping anything a list may not hold. */
export function entriesOf(value: SettingValue): ScalarSettingValue[] {
  return Array.isArray(value) ? value.filter(isScalar) : [];
}

/** The value a fresh list entry starts at, inside whatever bounds the field declares. */
export function blankFor(field: SettingField, type: ScalarSettingType): ScalarSettingValue {
  if (type === "bool") return false;
  if (type === "choice") return field.choices?.[0] ?? "";
  if (type === "color") return field.choices?.[0] ?? "#000000";
  if (type === "int" || type === "float" || type === "duration_ms") return field.min ?? 0;
  return "";
}

/** A step that fits the range the activity declared, so a 0..1 float is not stepped by one. */
export function stepFor(field: SettingField, type: ScalarSettingType): number {
  const min = field.min ?? null;
  const max = field.max ?? null;
  const span = min === null || max === null ? null : max - min;
  if (type === "int") return 1;
  if (type === "duration_ms") return span !== null && span <= 1000 ? 10 : 100;
  if (span === null) return 0.1;
  if (span <= 1) return 0.01;
  if (span <= 10) return 0.1;
  return 1;
}

/** A number moved back inside the bounds the field declares. */
export function clampTo(field: SettingField, value: number): number {
  const min = field.min ?? null;
  const max = field.max ?? null;
  if (min !== null && value < min) return min;
  if (max !== null && value > max) return max;
  return value;
}

/** Seconds, as a person reads a duration a manifest declares in milliseconds. */
export function secondsOf(ms: number): string {
  return `${String(Math.round(ms / 100) / 10)}s`;
}

/** Why one scalar does not satisfy this field, in the order the sdk checks it. */
export function scalarRefusal(
  field: SettingField,
  type: ScalarSettingType,
  value: ScalarSettingValue,
): string | null {
  if (type === "bool") return typeof value === "boolean" ? null : "must be true or false";
  if (type === "int" || type === "float" || type === "duration_ms") {
    if (typeof value !== "number" || Number.isNaN(value)) return `must be a ${type} number`;
    if (type === "int" && !Number.isInteger(value)) return "must be a whole number";
    if (field.min != null && value < field.min) {
      return `is below its minimum of ${String(field.min)}`;
    }
    if (field.max != null && value > field.max) {
      return `is above its maximum of ${String(field.max)}`;
    }
    return null;
  }
  if (typeof value !== "string") return `must be ${type}`;
  if (type === "choice" && !(field.choices ?? []).includes(value)) {
    return `must be one of ${(field.choices ?? []).join(", ")}`;
  }
  if (type === "color" && !COLOR_PATTERN.test(value)) return "must be a #rrggbb color";
  return null;
}

/** Why this whole value does not satisfy this field, a list entry by entry. */
export function settingRefusal(field: SettingField, value: SettingValue): string | null {
  if (field.type === "list") {
    if (!Array.isArray(value)) return "must be a list";
    const of = scalarTypeOf(field);
    for (const [index, entry] of value.entries()) {
      const refusal = scalarRefusal(field, of, entry);
      if (refusal !== null) return `entry ${String(index + 1)} ${refusal}`;
    }
    return null;
  }
  if (Array.isArray(value)) return `must be ${field.type}`;
  return scalarRefusal(field, field.type, value);
}

/** One entry moved by `by` places, or the same list when it cannot move that way. */
export function moved(entries: readonly ScalarSettingValue[], at: number, by: number): ScalarSettingValue[] {
  const to = at + by;
  if (to < 0 || to >= entries.length) return [...entries];
  const out = [...entries];
  const held = out[at];
  const other = out[to];
  if (held === undefined || other === undefined) return out;
  out[at] = other;
  out[to] = held;
  return out;
}

/* --------------------------------------------------------- pair-string settings, `k=v,k=v` */

/** The one shape a `key=value` list may take. */
export const PAIR_LIST_PATTERN = /^\s*[^=,\s]+=[^=,]*(,[^=,\s]+=[^=,]*)*\s*$/;

export interface PairRow {
  readonly key: string;
  readonly value: string;
}

/** `k=v,k=v` split into ordered rows, `[]` for an empty string, null when it does not parse. */
export function parsePairString(raw: string): PairRow[] | null {
  const trimmed = raw.trim();
  if (trimmed === "") return [];
  if (!PAIR_LIST_PATTERN.test(trimmed)) return null;
  return trimmed.split(",").map((part) => {
    const at = part.indexOf("=");
    return { key: part.slice(0, at), value: part.slice(at + 1) };
  });
}

/** The inverse of `parsePairString`: rows back to `k=v,k=v`, in the order given. */
export function serializePairString(rows: readonly PairRow[]): string {
  return rows.map((row) => `${row.key}=${row.value}`).join(",");
}

function helpNamesPairs(field: SettingField): boolean {
  return /\bpairs?\b/i.test(field.help ?? "");
}

/** A `string` setting's rows when it is a `k=v,k=v` list, or null to fall back to `Input`. */
export function pairFieldRows(field: SettingField, value: SettingValue): PairRow[] | null {
  if (field.type !== "string" || isSupplied(field)) return null;
  const raw = typeof value === "string" ? value : "";
  if (raw.trim() !== "") return parsePairString(raw);
  const fallback = typeof field.default === "string" ? field.default.trim() : "";
  if (fallback !== "" && PAIR_LIST_PATTERN.test(fallback)) return [];
  return helpNamesPairs(field) ? [] : null;
}

/** Legal values for a pair's value column, when `help` enumerates them; null falls back to `Input`. */
export function pairValueChoices(field: SettingField): string[] | null {
  const match = /[a-z][a-z0-9_]*(?:,\s*[a-z][a-z0-9_]*){2,}\s+and\s+[a-z][a-z0-9_]*/.exec(
    field.help ?? "",
  );
  if (match === null) return null;
  const tokens = match[0]
    .replace(/\s+and\s+/, ",")
    .split(",")
    .map((token) => token.trim())
    .filter((token) => token !== "");
  const unique = Array.from(new Set(tokens));
  return unique.length >= 2 ? unique : null;
}
